import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { useUpdateCryptoOnchainPlatformSettings } from '@/lib/api/crypto-onchain-platform';

import { formatPercent, MAX_SHARE_PERCENT, parsePercentInput } from './format';
import type { PlatformSettings } from './types';

type Props = {
  settings: PlatformSettings | null;
};

/**
 * The default platform share, as a percent (the DTO carries a percent; the
 * server turns it into basis points). Slider and text field drive the same
 * string state so a keyboard-precise value and a quick drag both work. The
 * parent re-keys this component on the saved value, so there is no effect
 * copying server data into state.
 */
export function RevenueShareForm({ settings }: Props) {
  const { t, i18n } = useTranslation('manageCryptoPayments');
  const { isSuperAdmin } = useSessionClaims();
  const updateSettings = useUpdateCryptoOnchainPlatformSettings();

  const currentPercent = settings?.defaultSharePercent ?? 0;
  const [percentInput, setPercentInput] = useState(String(currentPercent));

  const parsedPercent = parsePercentInput(percentInput);
  const isValid = parsedPercent !== null;
  const isDirty = parsedPercent !== null && parsedPercent !== currentPercent;
  const canSubmit = isDirty && !!settings?.configured && !updateSettings.isPending;
  const currentLabel = formatPercent(currentPercent, i18n.language);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (parsedPercent === null || !canSubmit) return;
    updateSettings.mutate({ defaultSharePercent: parsedPercent });
  };

  const handleSliderChange = ([value]: number[]) => {
    setPercentInput(String(value));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('share.title')}</CardTitle>
        <CardDescription>{t('share.description')}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {isSuperAdmin ? (
          <form onSubmit={handleSubmit} className="space-y-4" aria-label={t('share.title')}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <Slider
                aria-label={t('share.sliderLabel')}
                min={0}
                max={MAX_SHARE_PERCENT}
                step={0.01}
                value={[isValid ? parsedPercent : currentPercent]}
                onValueChange={handleSliderChange}
                disabled={updateSettings.isPending}
                className="sm:flex-1"
              />
              <div className="flex items-center gap-2">
                <Label htmlFor="default-share-percent" className="sr-only">
                  {t('share.inputLabel')}
                </Label>
                <Input
                  id="default-share-percent"
                  inputMode="decimal"
                  value={percentInput}
                  onChange={(event) => setPercentInput(event.target.value)}
                  className="w-28 text-right font-mono"
                  aria-invalid={!isValid || undefined}
                  disabled={updateSettings.isPending}
                />
                <span className="text-muted-foreground text-sm">%</span>
              </div>
            </div>

            {!isValid && <p className="text-destructive text-xs">{t('share.invalid')}</p>}

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={!canSubmit}>
                {updateSettings.isPending ? t('actions.saving') : t('share.save')}
              </Button>
              <span className="text-muted-foreground text-xs">
                {t('share.current', { percent: currentLabel })}
              </span>
            </div>
          </form>
        ) : (
          <div className="space-y-1">
            <p className="text-2xl font-semibold tabular-nums">{currentLabel}</p>
            <p className="text-muted-foreground text-xs">{t('share.superadminOnly')}</p>
          </div>
        )}

        <p className="text-muted-foreground text-xs leading-relaxed">{t('share.explanation')}</p>
      </CardContent>
    </Card>
  );
}
